import Icon, { IconName } from '@components/utils/icons/icon';
import Ripple from '@components/utils/ripple';
import { StyledProps, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import React, {
    ComponentPropsWithoutRef,
    CSSProperties,
    ElementType,
    useMemo,
} from 'react';

import { getButtonStyle, getRippleColor } from './getButtonStyle';
import { Spinner } from '../../feedback';

export interface ButtonStylingProps {
    isLoading?: boolean;
    disabled?: boolean;
    disableRipple?: boolean;
    isFullWidth?: boolean;
    buttonSize?: 'xsmall' | 'small' | 'medium' | 'large';
    colorScheme?: 'black' | 'white' | 'blackAlpha' | 'whiteAlpha';
    variation?: 'primary' | 'secondary';
    variant?: 'solid' | 'outline' | 'underline' | 'ghost';
    leftIcon?: IconName;
    rightIcon?: IconName;
    rounded?: 'full' | CSSProperties['borderRadius'];
    px?: CSSProperties['paddingInline'];
    isMultiline?: boolean;
}

export type ButtonProps<T extends ElementType = 'button'> =
    PolymorphicProps<T> & ButtonStylingProps;

const StyledButton = styled.button<StyledProps<StyledProps>>`
    ${getButtonStyle}
`;

type PolymorphicProps<T extends ElementType = 'button'> = {
    as?: T;
} & ComponentPropsWithoutRef<T>;

export const Button = <T extends ElementType = 'button'>({
    children,
    as = 'button' as T,
    isLoading = false,
    disableRipple,
    colorScheme = 'black',
    leftIcon,
    rightIcon,
    variant = 'solid',
    ...props
}: ButtonProps<T>) => {
    const { palette } = useTheme();
    const isDisabled = useMemo(
        () => (isLoading ? true : !!props.disabled),
        [isLoading, props.disabled]
    );
    const iconColor = useMemo(() => {
        if (variant === 'solid') {
            return palette.common[
                colorScheme.includes('black') ? 'white' : 'black'
            ];
        }

        return palette.common[
            colorScheme.includes('black') ? 'white' : 'black'
        ];
    }, [colorScheme, palette.common, variant]);

    return (
        <StyledButton
            {...props}
            as={as}
            disabled={isDisabled}
            isLoading={isLoading}
            colorScheme={colorScheme}
            variant={variant}
        >
            {isLoading ? (
                <Spinner color={iconColor} />
            ) : (
                <>
                    {leftIcon && (
                        <Icon
                            name={leftIcon}
                            color={iconColor}
                            size={props.buttonSize}
                        />
                    )}
                    {children}
                    {rightIcon && (
                        <Icon
                            name={rightIcon}
                            color={iconColor}
                            size={props.buttonSize}
                        />
                    )}
                </>
            )}
            {!disableRipple && (
                <Ripple
                    color={getRippleColor({
                        variant: variant,
                        colorScheme: colorScheme,
                    })}
                />
            )}
        </StyledButton>
    );
};
